import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Print,
  Settings,
  Calculate,
  FileUpload,
  Clear,
  CheckCircle
} from '@mui/icons-material';
import { COLORS, withOpacity } from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import { uploadSTLFile, submitQuoteRequest } from '../../services/quoteService';
import LoginModal from '../Auth/LoginModal';

const ThreeDPrintingQuote = () => {
  const { user } = useAuth();
  
  // File and model state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [modelAnalysis, setModelAnalysis] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Print settings state
  const [printSettings, setPrintSettings] = useState({
    material: 'PLA',
    layerHeight: 0.2,
    infill: 20,
    printSpeed: 50,
    supports: false,
    brim: false
  });
  
  // Model units state
  const [modelUnits, setModelUnits] = useState('mm'); // 'mm' or 'inches'
  
  // Quote state
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Quote request state
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingQuoteData, setPendingQuoteData] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');

  // Material options with pricing (₹/kg)
  const materials = {
    'PLA': { name: 'PLA', price: 800, color: '#4CAF50' },
    'ABS': { name: 'ABS', price: 1000, color: '#FF9800' },
    'PETG': { name: 'PETG', price: 1200, color: '#2196F3' },
    'TPU': { name: 'TPU (Flexible)', price: 3500, color: '#9C27B0' }
    // Easy to add more materials in the future:
    // 'NYLON': { name: 'Nylon', price: 2000, color: '#607D8B' },
    // 'WOOD': { name: 'Wood Fill', price: 1500, color: '#795548' }
  };

  // Automatically calculate quote when settings change or file is uploaded
  useEffect(() => {
    if (uploadedFile && modelAnalysis) {
      calculateQuote();
    }
  }, [printSettings, modelAnalysis, modelUnits]);

  // Handle file upload with validation
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      // Clear previous analysis and quote
      setModelAnalysis(null);
      setQuote(null);
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.stl')) {
        alert('Please upload a valid STL file (.stl extension required)');
        return;
      }
      
      // Validate file size (max 150MB)
      if (file.size > 150 * 1024 * 1024) {
        alert('File size too large. Please upload a file smaller than 150MB.');
        return;
      }
      
      setUploadedFile(file);
      processSTLFile(file);
    }
  }, []);

  // Process STL file with proper parsing
  const processSTLFile = async (file) => {
    setIsProcessing(true);
    
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result;
          const analysis = parseSTLFile(arrayBuffer);
          
          setModelAnalysis(analysis);
          setIsProcessing(false);
        } catch (parseError) {
          console.error('STL parsing error:', parseError);
          setIsProcessing(false);
          alert(`Error parsing STL file: ${parseError.message}`);
        }
      };
      
      reader.onerror = () => {
        setIsProcessing(false);
        alert('Error reading file. Please try again.');
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error processing STL:', error);
      setIsProcessing(false);
      alert('Error processing STL file. Please check the file and try again.');
    }
  };

  // Parse STL file to get accurate dimensions and properties
  const parseSTLFile = (buffer) => {
    try {
      const dataView = new DataView(buffer);
      
      // Check if it's binary STL (has 80-byte header + 4-byte triangle count)
      const isBinary = buffer.byteLength > 84;
      
      let vertices = [];
      let triangleCount = 0;
      
      if (isBinary && buffer.byteLength >= 84) {
        // Binary STL parsing
        triangleCount = dataView.getUint32(80, true); // little-endian
        let offset = 84; // Skip header (80 bytes) + triangle count (4 bytes)
        
        // Validate expected file size
        const expectedSize = 84 + (triangleCount * 50); // 50 bytes per triangle
        if (buffer.byteLength < expectedSize) {
          throw new Error('Invalid binary STL file: file size mismatch');
        }
        
        for (let i = 0; i < triangleCount && offset < buffer.byteLength - 50; i++) {
          // Skip normal vector (12 bytes)
          offset += 12;
          
          // Read 3 vertices (9 floats = 36 bytes)
          for (let j = 0; j < 3; j++) {
            const x = dataView.getFloat32(offset, true);
            const y = dataView.getFloat32(offset + 4, true);
            const z = dataView.getFloat32(offset + 8, true);
            
            // Validate that coordinates are reasonable numbers
            if (!isFinite(x) || !isFinite(y) || !isFinite(z)) {
              console.warn(`Invalid vertex coordinates at triangle ${i}, vertex ${j}`);
              continue;
            }
            
            vertices.push({ x, y, z });
            offset += 12;
          }
          
          // Skip attribute byte count (2 bytes)
          offset += 2;
        }
      } else {
        // ASCII STL parsing
        const text = new TextDecoder().decode(buffer);
        const lines = text.split('\n');
        
        for (let line of lines) {
          line = line.trim();
          if (line.startsWith('vertex')) {
            const coords = line.split(/\s+/);
            if (coords.length >= 4) {
              const x = parseFloat(coords[1]);
              const y = parseFloat(coords[2]);
              const z = parseFloat(coords[3]);
              
              // Validate coordinates
              if (isFinite(x) && isFinite(y) && isFinite(z)) {
                vertices.push({ x, y, z });
              }
            }
          }
        }
        triangleCount = Math.floor(vertices.length / 3);
      }
      
      // Validate we have vertices
      if (vertices.length === 0) {
        throw new Error('No valid vertices found in STL file');
      }
      
      // Calculate bounding box
      let minX = vertices[0].x, maxX = vertices[0].x;
      let minY = vertices[0].y, maxY = vertices[0].y;
      let minZ = vertices[0].z, maxZ = vertices[0].z;
      
      for (let vertex of vertices) {
        minX = Math.min(minX, vertex.x);
        maxX = Math.max(maxX, vertex.x);
        minY = Math.min(minY, vertex.y);
        maxY = Math.max(maxY, vertex.y);
        minZ = Math.min(minZ, vertex.z);
        maxZ = Math.max(maxZ, vertex.z);
      }
      
      // Calculate dimensions in mm (assuming STL is in mm)
      const dimensions = {
        x: maxX - minX,
        y: maxY - minY,
        z: maxZ - minZ
      };
      
      // Validate dimensions are reasonable
      if (dimensions.x <= 0 || dimensions.y <= 0 || dimensions.z <= 0) {
        throw new Error('Invalid model dimensions detected');
      }
      
      // Check if dimensions seem too large (> 1000mm = 1m)
      if (dimensions.x > 1000 || dimensions.y > 1000 || dimensions.z > 1000) {
        console.warn('Model dimensions are very large. Units might not be in millimeters.');
      }
      
      // Calculate approximate volume using bounding box (rough estimate)
      const boundingVolume = dimensions.x * dimensions.y * dimensions.z;
      
      // Convert to cm³ for volume calculations
      const volumeCm3 = boundingVolume / 1000;
      
      // Calculate surface area (rough estimate)
      const surfaceArea = 2 * (dimensions.x * dimensions.y + dimensions.y * dimensions.z + dimensions.x * dimensions.z);
      
      return {
        volume: Math.max(volumeCm3, 0.1), // Ensure minimum volume
        surfaceArea: surfaceArea / 100, // Convert to cm²
        boundingBox: dimensions, // Keep in mm for display
        triangles: triangleCount,
        vertices: vertices.length
      };
    } catch (error) {
      console.error('Error parsing STL file:', error);
      throw new Error(`Failed to parse STL file: ${error.message}`);
    }
  };

  // Calculate printing quote
  const calculateQuote = () => {
    if (!modelAnalysis) return;
    
    setIsCalculating(true);
    
    // Mock calculation (in real implementation, you'd use slicing engine)
    setTimeout(() => {
      const volumeInCm3 = modelAnalysis.volume; // Already in cm³ from processSTLFile
      const material = materials[printSettings.material];
      
      // Note: The volume is calculated from the STL dimensions. 
      // If the user selects inches, it means the STL coordinates should be interpreted as inches
      // This affects the calculation in processSTLFile, but for now we'll handle it here
      let finalVolumeInCm3 = volumeInCm3;
      if (modelUnits === 'inches') {
        // If user says the model units are inches, we need to recalculate volume
        // The bounding box is in whatever units the STL uses, so convert it properly
        const mmDimensions = modelAnalysis.boundingBox; // These are the raw STL dimensions
        const inchDimensions = {
          x: mmDimensions.x, // Treat as inches
          y: mmDimensions.y,
          z: mmDimensions.z
        };
        // Convert to mm: inches × 25.4 = mm
        const mmFromInches = {
          x: inchDimensions.x * 25.4,
          y: inchDimensions.y * 25.4,
          z: inchDimensions.z * 25.4
        };
        // Calculate volume in mm³, then convert to cm³
        const volumeMm3 = mmFromInches.x * mmFromInches.y * mmFromInches.z;
        finalVolumeInCm3 = volumeMm3 / 1000;
      }
      
      // Updated densities based on actual material specifications (g/cm³)
      const densities = {
        'PLA': 1.24,    // 1.24 to 1.25 g/cm³
        'ABS': 1.11,    // 1.02 to 1.21 g/cm³ (using middle value)
        'PETG': 1.33,   // 1.27 to 1.38 g/cm³ (using middle value)
        'TPU': 1.18     // 1.1 to 1.25 g/cm³ (using middle value)
      };
      
      const density = densities[printSettings.material] || 1.24;
      const weightInGrams = finalVolumeInCm3 * density;
      const weightInKg = weightInGrams / 1000;
      
      // Base calculations
      const materialCost = weightInKg * material.price; // ₹/kg pricing
      const layerHeightMultiplier = printSettings.layerHeight === 0.1 ? 1.5 : 
                                   printSettings.layerHeight === 0.2 ? 1.0 : 0.8;
      const infillMultiplier = 1 + (printSettings.infill / 100);
      const supportsCost = printSettings.supports ? materialCost * 0.3 : 0;
      const brimCost = printSettings.brim ? materialCost * 0.1 : 0;
      
      // Realistic print time calculation based on actual slicing principles
      const calculatePrintTime = () => {
        // Get model dimensions
        const dimensions = modelAnalysis.boundingBox; // in mm
        let height, width, length;
        
        if (modelUnits === 'inches') {
          height = dimensions.z * 25.4; // Convert to mm
          width = dimensions.y * 25.4;
          length = dimensions.x * 25.4;
        } else {
          height = dimensions.z;
          width = dimensions.y;
          length = dimensions.x;
        }
        
        // Calculate number of layers
        const numberOfLayers = Math.ceil(height / printSettings.layerHeight);
        
        // Estimate perimeter length per layer (simplified rectangular approximation)
        const perimeterPerLayer = 2 * (length + width); // mm
        
        // More accurate infill path calculation
        const layerArea = length * width; // mm²
        const infillDensity = printSettings.infill / 100;
        
        // Estimate infill path based on area and density
        // For 100% infill, we need to fill the entire area minus perimeter
        // Typical infill line width is about 0.4mm, spacing for 100% is ~0.4mm
        const infillLineWidth = 0.4; // mm
        const effectiveInfillArea = layerArea * infillDensity;
        
        // Calculate infill path length more realistically
        // For rectangular infill pattern, path length ≈ area / line_width
        const infillPathPerLayer = effectiveInfillArea / infillLineWidth;
        
        // Total extrusion path length
        const totalExtrusionLength = numberOfLayers * (perimeterPerLayer + infillPathPerLayer);
        
        // Convert print speed from UI (assumed mm/s) to actual mm/s
        const printSpeedMmPerSec = printSettings.printSpeed; // Assuming this is mm/s
        
        // Calculate printing time (extrusion only)
        const extrusionTimeSeconds = totalExtrusionLength / printSpeedMmPerSec;
        
        // Add non-printing time (travel moves, layer changes, heating, etc.)
        // Reduced travel time as the original was too conservative
        const travelTimePerLayer = 1; // seconds per layer for travel moves
        const totalTravelTime = numberOfLayers * travelTimePerLayer;
        
        // Add time for supports if enabled
        const supportTime = printSettings.supports ? extrusionTimeSeconds * 0.3 : 0;
        
        // Total time in seconds
        const totalTimeSeconds = extrusionTimeSeconds + totalTravelTime + supportTime;
        
        // Convert to hours
        const printTimeHours = totalTimeSeconds / 3600;
        
        return printTimeHours;
      };
      
      const printTimeHours = calculatePrintTime();
      
      // Detailed labor and machine cost calculation
      // Components:
      // 1. Electricity: 1300W printer × printing hours × ₹10/kWh
      // 2. Printer depreciation: ₹3/hour
      // 3. Labor supervision: ₹20/hour
      const electricityCost = 1.3 * printTimeHours * 10; // 1300W printer, ₹10/kWh
      const printerDepreciationCost = 3 * printTimeHours; // ₹3/hour
      const laborCharge = 20 * printTimeHours; // ₹20/hour
      
      // Total labor and machine cost
      const laborCost = electricityCost + printerDepreciationCost + laborCharge;
      
      const subtotal = materialCost + supportsCost + brimCost + laborCost;
      const taxes = subtotal * 0.18; // 18% GST
      const total = subtotal + taxes;
      
      setQuote({
        materialCost: materialCost,
        supportsCost: supportsCost,
        brimCost: brimCost,
        laborCost: laborCost,
        subtotal: subtotal,
        taxes: taxes,
        total: total,
        printTime: printTimeHours,
        material: material.name,
        weight: weightInGrams
      });
      
      setIsCalculating(false);
    }, 1000);
  };

  // Helper function to format dimensions based on units
  const formatDimension = (mmValue) => {
    if (modelUnits === 'inches') {
      return (mmValue / 25.4).toFixed(2);
    }
    return mmValue.toFixed(1);
  };

  // Clear current upload and start over
  const clearUpload = () => {
    setUploadedFile(null);
    setModelAnalysis(null);
    setQuote(null);
    setIsProcessing(false);
    setIsCalculating(false);
    // Reset units to default
    setModelUnits('mm');
  };

  // Handle quote request
  const handleQuoteRequest = async () => {
    if (!uploadedFile || !modelAnalysis || !quote) {
      setErrorMessage('Please upload a file and calculate a quote first');
      setSnackbarOpen(true);
      return;
    }

    if (!user) {
      // Store quote data for after login
      setPendingQuoteData({
        file: uploadedFile,
        modelAnalysis,
        printSettings,
        quote,
        customerNotes
      });
      setShowLoginModal(true);
      return;
    }

    await submitQuote();
  };

  // Submit quote request
  const submitQuote = async () => {
    setIsSubmittingQuote(true);
    try {
      // Upload STL file to storage
      const uploadResult = await uploadSTLFile(uploadedFile);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload file');
      }

      // Prepare quote data
      const quoteData = {
        modelUrl: uploadResult.url,
        fileName: uploadResult.fileName,
        modelAnalysis,
        printSettings: {
          ...printSettings,
          modelUnits
        },
        quote,
        customerNotes
      };

      // Submit quote request
      const result = await submitQuoteRequest(quoteData);
      
      if (result.success) {
        setSuccessDialog(true);
        // Clear the form
        clearUpload();
        setCustomerNotes('');
      } else {
        throw new Error(result.message || 'Failed to submit quote request');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      setErrorMessage(error.message || 'Failed to submit quote request');
      setSnackbarOpen(true);
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  // Handle successful login
  const handleLoginSuccess = async () => {
    if (pendingQuoteData) {
      // Auto-submit the quote after successful login
      await submitQuote();
      setPendingQuoteData(null);
    }
  };

  // Check for pending quote data on component mount or user change
  useEffect(() => {
    // Check session storage for pending quote data
    const storedQuoteData = sessionStorage.getItem('pendingQuoteData');
    if (storedQuoteData && user) {
      try {
        const quoteData = JSON.parse(storedQuoteData);
        setPendingQuoteData(quoteData);
        sessionStorage.removeItem('pendingQuoteData');
        // Auto-submit after a short delay to ensure everything is loaded
        setTimeout(() => {
          submitQuote();
        }, 1000);
      } catch (error) {
        console.error('Error parsing stored quote data:', error);
        sessionStorage.removeItem('pendingQuoteData');
      }
    }
  }, [user]);

  // Store quote data when showing login modal
  useEffect(() => {
    if (showLoginModal && pendingQuoteData) {
      sessionStorage.setItem('pendingQuoteData', JSON.stringify(pendingQuoteData));
    }
  }, [showLoginModal, pendingQuoteData]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={4} color={COLORS.text.primary}>
        3D Printing Quote Calculator
      </Typography>
      
      <Grid container spacing={4}>
        {/* Left Column - File Upload */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${COLORS.border.light}`, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Upload Your Model
              </Typography>
              {uploadedFile && (
                <IconButton onClick={clearUpload} color="error" size="small">
                  <Clear />
                </IconButton>
              )}
            </Box>
            
            {!uploadedFile ? (
              <Box
                sx={{
                  border: `2px dashed ${COLORS.border.light}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: COLORS.primary.main,
                    backgroundColor: withOpacity(COLORS.primary.main, 0.05)
                  }
                }}
                component="label"
              >
                <input
                  type="file"
                  accept=".stl"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 48, color: COLORS.text.secondary, mb: 2 }} />
                <Typography variant="h6" color={COLORS.text.secondary} gutterBottom>
                  Drop your STL file here or click to browse
                </Typography>
                <Typography variant="body2" color={COLORS.text.secondary}>
                  Supported format: STL (max 150MB)
                </Typography>
                
                {/* Unit Selection */}
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    Model Units:
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={modelUnits}
                      onChange={(e) => setModelUnits(e.target.value)}
                    >
                      <FormControlLabel 
                        value="mm" 
                        control={<Radio />} 
                        label="Millimeters (mm)" 
                      />
                      <FormControlLabel 
                        value="inches" 
                        control={<Radio />} 
                        label="Inches" 
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            ) : (
              <Card elevation={0} sx={{ border: `1px solid ${COLORS.border.light}` }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Print color="primary" />
                    <Box flexGrow={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {uploadedFile.name}
                      </Typography>
                      <Typography variant="body2" color={COLORS.text.secondary}>
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <Chip
                      label={isProcessing ? "Processing..." : modelAnalysis ? "Ready" : "Analyzing"}
                      color={isProcessing ? "warning" : modelAnalysis ? "success" : "info"}
                      size="small"
                    />
                  </Box>
                  
                  {isProcessing && (
                    <Box mt={2}>
                      <LinearProgress />
                      <Typography variant="body2" color={COLORS.text.secondary} mt={1}>
                        Analyzing your 3D model...
                      </Typography>
                    </Box>
                  )}
                  
                  {modelAnalysis && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Model Analysis:
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Volume:</strong> {modelAnalysis.volume.toFixed(2)} cm³
                          </Typography>
                          <Typography variant="body2">
                            <strong>Surface Area:</strong> {modelAnalysis.surfaceArea.toFixed(2)} cm²
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Dimensions ({modelUnits}):</strong><br />
                            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                              L: {formatDimension(modelAnalysis.boundingBox.x)}<br />
                              W: {formatDimension(modelAnalysis.boundingBox.y)}<br />
                              H: {formatDimension(modelAnalysis.boundingBox.z)}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Model Info:</strong><br />
                            Triangles: {modelAnalysis.triangles.toLocaleString()}<br />
                            Vertices: {modelAnalysis.vertices.toLocaleString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
        
        {/* Right Column - Print Settings */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${COLORS.border.light}`, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
              Print Settings
            </Typography>
            
            <Grid container spacing={3}>
              {/* Material Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Material</InputLabel>
                  <Select
                    value={printSettings.material}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, material: e.target.value }))}
                    label="Material"
                  >
                    {Object.entries(materials).map(([key, material]) => (
                      <MenuItem key={key} value={key}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: material.color
                            }}
                          />
                          {material.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Layer Height */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Layer Height: {printSettings.layerHeight}mm
                </Typography>
                <Slider
                  value={printSettings.layerHeight}
                  onChange={(e, value) => setPrintSettings(prev => ({ ...prev, layerHeight: value }))}
                  min={0.1}
                  max={0.3}
                  step={0.1}
                  marks={[
                    { value: 0.1, label: '0.1mm (High)' },
                    { value: 0.2, label: '0.2mm (Standard)' },
                    { value: 0.3, label: '0.3mm (Fast)' }
                  ]}
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              {/* Infill */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Infill: {printSettings.infill}%
                </Typography>
                <Slider
                  value={printSettings.infill}
                  onChange={(e, value) => setPrintSettings(prev => ({ ...prev, infill: value }))}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 20, label: '20%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' }
                  ]}
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              {/* Print Speed */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Print Speed: {printSettings.printSpeed}mm/s
                </Typography>
                <Slider
                  value={printSettings.printSpeed}
                  onChange={(e, value) => setPrintSettings(prev => ({ ...prev, printSpeed: value }))}
                  min={20}
                  max={100}
                  step={10}
                  marks={[
                    { value: 20, label: 'Slow' },
                    { value: 50, label: 'Standard' },
                    { value: 100, label: 'Fast' }
                  ]}
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              {/* Additional Options */}
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Supports</InputLabel>
                  <Select
                    value={printSettings.supports}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, supports: e.target.value }))}
                    label="Supports"
                  >
                    <MenuItem value={false}>No Supports</MenuItem>
                    <MenuItem value={true}>Generate Supports</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Adhesion</InputLabel>
                  <Select
                    value={printSettings.brim}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, brim: e.target.value }))}
                    label="Adhesion"
                  >
                    <MenuItem value={false}>No Brim</MenuItem>
                    <MenuItem value={true}>Add Brim</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Quote Display */}
        {(quote || isCalculating) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${COLORS.border.light}`, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                <Calculate sx={{ mr: 1, verticalAlign: 'middle' }} />
                Printing Quote
              </Typography>
              
              {isCalculating ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={24} />
                  <Typography>Calculating quote...</Typography>
                </Box>
              ) : quote && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color={COLORS.text.secondary} gutterBottom>
                        Print Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Material:</strong> {quote.material}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Estimated Weight:</strong> {quote.weight.toFixed(2)}g
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Print Time:</strong> {quote.printTime.toFixed(1)} hours
                          </Typography>
                          <Typography variant="body2">
                            <strong>Layer Height:</strong> {printSettings.layerHeight}mm
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color={COLORS.text.secondary} gutterBottom>
                        Cost Breakdown
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="body2">Material Cost:</Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <Typography variant="body2">₹{quote.materialCost.toFixed(2)}</Typography>
                        </Grid>
                        
                        {quote.supportsCost > 0 && (
                          <>
                            <Grid item xs={8}>
                              <Typography variant="body2">Support Material:</Typography>
                            </Grid>
                            <Grid item xs={4} textAlign="right">
                              <Typography variant="body2">₹{quote.supportsCost.toFixed(2)}</Typography>
                            </Grid>
                          </>
                        )}
                        
                        {quote.brimCost > 0 && (
                          <>
                            <Grid item xs={8}>
                              <Typography variant="body2">Brim/Adhesion:</Typography>
                            </Grid>
                            <Grid item xs={4} textAlign="right">
                              <Typography variant="body2">₹{quote.brimCost.toFixed(2)}</Typography>
                            </Grid>
                          </>
                        )}
                        
                        <Grid item xs={8}>
                          <Typography variant="body2">Labor & Machine Time:</Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <Typography variant="body2">₹{quote.laborCost.toFixed(2)}</Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        
                        <Grid item xs={8}>
                          <Typography variant="body2">Subtotal:</Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <Typography variant="body2">₹{quote.subtotal.toFixed(2)}</Typography>
                        </Grid>
                        
                        <Grid item xs={8}>
                          <Typography variant="body2">Taxes (18% GST):</Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <Typography variant="body2">₹{quote.taxes.toFixed(2)}</Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                            <Typography variant="caption">
                              This is a tentative quote based on automated calculations. Actual pricing may vary depending on model complexity, finishing requirements, and current material costs. Our representative will contact you with the final quote.
                            </Typography>
                          </Alert>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: COLORS.primary.main, color: 'white', textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Total Quote
                        </Typography>
                        <Typography variant="h3" fontWeight={700}>
                          ₹{quote.total.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block', fontStyle: 'italic' }}>
                          *This is a tentative quote. Actual price may vary.
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                          Estimated delivery: 3-5 business days
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
                          Our representative will contact you for final confirmation.
                        </Typography>
                        
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleQuoteRequest}
                          disabled={isSubmittingQuote || !quote}
                          sx={{
                            mt: 2,
                            backgroundColor: 'white',
                            color: COLORS.primary.main,
                            '&:hover': {
                              backgroundColor: withOpacity('#ffffff', 0.9)
                            }
                          }}
                        >
                          {isSubmittingQuote ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={20} color="inherit" />
                              Submitting...
                            </Box>
                          ) : (
                            'Request Quote'
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        )}
        
        {/* Instructions */}
        {!uploadedFile && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                How to get your 3D printing quote:
              </Typography>
              <Typography variant="body2">
                1. Upload your STL file (we'll analyze it automatically)<br />
                2. Adjust print settings to your preferences<br />
                3. Get an instant quote with cost breakdown<br />
                4. Click "Request Quote" to proceed with your order
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Customer Notes Section - Show only when quote is available */}
        {quote && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${COLORS.border.light}`, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Additional Notes (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Any special requirements, color preferences, delivery instructions, etc."
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccessCallback={handleLoginSuccess}
      />

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ color: COLORS.primary.main }} />
            <Typography variant="h6">Quote Request Submitted!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Your 3D printing quote request has been successfully submitted. Our team will review your request and get back to you within 24 hours.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll receive updates via email and can track your quote status in your account dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSuccessDialog(false)}
            variant="contained"
            sx={{ backgroundColor: COLORS.primary.main }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ThreeDPrintingQuote;
